import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import { ArrowLeft, Phone, CheckCircle, Shield, Calendar, Save } from 'lucide-react';
import { useCallRouting } from '../../hooks/phone/useCallRouting';
import { usePhoneNumbers } from '../../hooks/phone/usePhoneNumbers';
import { useOtpVerification } from '../../hooks/phone/useOtpVerification';
import { RoutingRule } from '../../types/phone-numbers';
import { RootState } from '../../store';
import Notifications from '../../components/common/Notifications';
import { NotificationT, SEVERITY } from '../../types/notification';
import { ROUTES } from '../../config/constants/routes';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

interface DaySchedule {
  day: string;
  startTime: string;
  endTime: string;
  action: 'FORWARD' | 'AI_AGENT';
  enabled: boolean;
  ruleId?: number;
}

const buildDefaultSchedule = (): DaySchedule[] =>
  DAYS_OF_WEEK.map(day => ({
    day,
    startTime: '09:00',
    endTime: '17:00',
    action: 'FORWARD' as const,
    enabled: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].includes(day),
  }));

const parseDaysOfWeek = (raw: unknown): string[] => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // not JSON, treat as single day
      return [raw];
    }
  }
  return [];
};

const buildScheduleFromRules = (rules: RoutingRule[]): DaySchedule[] => {
  const schedule = buildDefaultSchedule();

  for (const rule of rules) {
    for (const ruleDay of parseDaysOfWeek(rule.daysOfWeek)) {
      const normalized = ruleDay.charAt(0).toUpperCase() + ruleDay.slice(1).toLowerCase();
      const idx = schedule.findIndex(s => s.day === normalized);
      if (idx !== -1) {
        schedule[idx] = {
          day: normalized,
          startTime: rule.startTime,
          endTime: rule.endTime,
          action: rule.mode === 'AI_AGENT' ? 'AI_AGENT' : 'FORWARD',
          enabled: rule.enabled,
          ruleId: rule.id,
        };
      }
    }
  }

  return schedule;
};

const RoutingView = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { phoneNumberId } = useParams<{ phoneNumberId: string }>();
  const numericPhoneNumberId = Number(phoneNumberId);

  const orgTimezone = useSelector(
    (state: RootState) =>
      state.organizations.selectedOrganisation?.company?.timezone || 'Australia/Sydney'
  );

  const { phoneNumbers, fetchAll: fetchPhoneNumbers } = usePhoneNumbers();
  const {
    routingConfig,
    routingRules,
    isLoadingConfig,
    isLoadingRules,
    isSavingConfig,
    isSavingRule,
    error: routingError,
    fetchConfig,
    saveConfig,
    fetchRules,
    addRule,
    editRule,
    removeRule,
    triggerSync,
    clear,
  } = useCallRouting(numericPhoneNumberId);

  const {
    isSending,
    isVerifying,
    isVerified: otpVerified,
    error: otpError,
    cooldown,
    sendCode,
    verifyCode,
  } = useOtpVerification();

  const [phoneInput, setPhoneInput] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [verifiedNumber, setVerifiedNumber] = useState<string | null>(null);
  const [schedule, setSchedule] = useState<DaySchedule[]>(buildDefaultSchedule());
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  const phoneNumber = phoneNumbers.find(n => n.id === numericPhoneNumberId);

  useEffect(() => {
    if (!phoneNumbers.length) {
      fetchPhoneNumbers();
    }
    fetchConfig().catch(() => {});
    fetchRules().catch(() => {});

    return () => {
      clear();
    };
  }, [numericPhoneNumberId]);

  // Pre-populate verified state from existing config
  useEffect(() => {
    if (routingConfig?.forwardToNumber) {
      setVerifiedNumber(routingConfig.forwardToNumber);
      setPhoneInput(routingConfig.forwardToNumber);
    }
  }, [routingConfig]);

  // Build schedule from existing rules
  useEffect(() => {
    if (Array.isArray(routingRules) && routingRules.length > 0) {
      setSchedule(buildScheduleFromRules(routingRules));
    }
  }, [routingRules]);

  // When OTP verification succeeds
  useEffect(() => {
    if (otpVerified && phoneInput.trim()) {
      setVerifiedNumber(phoneInput.trim());
    }
  }, [otpVerified, phoneInput]);

  const isVerifiedState = !!verifiedNumber;
  const isLoading = isLoadingConfig || isLoadingRules;

  const handleSendCode = async () => {
    if (!phoneInput.trim()) return;
    await sendCode(phoneInput.trim());
    setCodeSent(true);
  };

  const handleVerifyCode = async () => {
    if (!otpCode.trim()) return;
    await verifyCode(phoneInput.trim(), otpCode.trim());
  };

  const updateScheduleRow = (index: number, updates: Partial<DaySchedule>) => {
    setSchedule(prev => prev.map((row, i) => (i === index ? { ...row, ...updates } : row)));
  };

  const handleSave = async () => {
    if (!verifiedNumber) return;
    setIsSaving(true);

    try {
      // 1. Save/update routing config
      await saveConfig({
        mode: 'FORWARD',
        forwardToNumber: verifiedNumber,
      });

      // 2. Process each day in the schedule
      for (let i = 0; i < schedule.length; i++) {
        const row = schedule[i];

        if (row.enabled) {
          const payload = {
            name: row.day,
            daysOfWeek: [row.day],
            startTime: row.startTime,
            endTime: row.endTime,
            mode: row.action,
            forwardToNumber: row.action === 'FORWARD' ? verifiedNumber : undefined,
            timezone: orgTimezone,
            priority: i,
            enabled: true,
          };

          if (row.ruleId) {
            await editRule(row.ruleId, payload);
          } else {
            await addRule(payload);
          }
        } else if (row.ruleId) {
          // Day disabled but has existing rule — delete it
          await removeRule(row.ruleId);
        }
      }

      // 3. Trigger a single Twilio sync after all saves complete
      await triggerSync();

      // Re-fetch to sync state
      await fetchRules().catch(() => {});

      setNotification({
        open: true,
        message: 'Call redirect settings saved and syncing.',
        severity: SEVERITY.SUCCESS,
      });
    } catch (err) {
      setNotification({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to save settings.',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const scheduleChanged = useMemo(() => {
    if (!Array.isArray(routingRules)) return true;
    const original = buildScheduleFromRules(routingRules);
    return JSON.stringify(schedule) !== JSON.stringify(original);
  }, [schedule, routingRules]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Tooltip title="Back to Phone Numbers">
          <IconButton size="small" onClick={() => navigate(ROUTES.PHONE)}>
            <ArrowLeft size={18} />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={500}>
            {phoneNumber ? phoneNumber.e164Number : `Phone Number #${phoneNumberId}`}
            {phoneNumber?.friendlyName && (
              <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 1 }}>
                · {phoneNumber.friendlyName}
              </Typography>
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Call redirect settings
          </Typography>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Section 1: Phone Verification */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Shield size={20} color={theme.palette.primary.main} />
              </Box>
              <Box>
                <Typography variant="h6">Phone Verification</Typography>
                <Typography variant="body2" color="text.secondary">
                  Verify the number where calls will be redirected
                </Typography>
              </Box>
            </Box>

            {isVerifiedState ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Chip
                  icon={<CheckCircle size={16} />}
                  label={`Verified: ${verifiedNumber}`}
                  color="success"
                  variant="outlined"
                  sx={{ fontSize: '0.875rem', py: 0.5 }}
                />
                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    setVerifiedNumber(null);
                    setPhoneInput('');
                    setCodeSent(false);
                    setOtpCode('');
                  }}
                >
                  Change
                </Button>
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                  <TextField
                    label="Phone Number"
                    placeholder="+61412345678"
                    size="small"
                    value={phoneInput}
                    onChange={e => {
                      setPhoneInput(e.target.value);
                      setCodeSent(false);
                      setOtpCode('');
                    }}
                    sx={{ flex: 1, maxWidth: 300 }}
                    helperText="E.164 format with country code"
                  />
                  <Button
                    variant="outlined"
                    onClick={handleSendCode}
                    disabled={!phoneInput.trim() || isSending || cooldown > 0}
                    sx={{ alignSelf: 'flex-start', mt: '1px' }}
                  >
                    {isSending ? (
                      <CircularProgress size={18} />
                    ) : cooldown > 0 ? (
                      `Resend (${cooldown}s)`
                    ) : codeSent ? (
                      'Resend Code'
                    ) : (
                      'Send Code'
                    )}
                  </Button>
                </Box>

                {codeSent && (
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                    <TextField
                      label="Verification Code"
                      placeholder="12345"
                      size="small"
                      value={otpCode}
                      onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                      sx={{ maxWidth: 160 }}
                      inputProps={{ maxLength: 5 }}
                    />
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={handleVerifyCode}
                      disabled={otpCode.length < 5 || isVerifying}
                      sx={{ alignSelf: 'flex-start', mt: '1px' }}
                    >
                      {isVerifying ? <CircularProgress size={18} color="inherit" /> : 'Verify'}
                    </Button>
                  </Box>
                )}

                {otpError && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {otpError}
                  </Alert>
                )}
              </Box>
            )}
          </Paper>

          {/* Section 2: Weekly Redirect Schedule (only after verification) */}
          {isVerifiedState && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Calendar size={20} color={theme.palette.secondary.main} />
                </Box>
                <Box>
                  <Typography variant="h6">Weekly Redirect Schedule</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configure when and how calls are redirected for each day
                  </Typography>
                </Box>
              </Box>

              {routingError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {routingError}
                </Alert>
              )}

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={50} align="center">
                        On
                      </TableCell>
                      <TableCell width={120}>Day</TableCell>
                      <TableCell width={160}>Start Time</TableCell>
                      <TableCell width={160}>End Time</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {schedule.map((row, index) => (
                      <TableRow
                        key={row.day}
                        sx={{
                          opacity: row.enabled ? 1 : 0.5,
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell align="center">
                          <Switch
                            size="small"
                            checked={row.enabled}
                            onChange={e => updateScheduleRow(index, { enabled: e.target.checked })}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {row.day}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            size="small"
                            value={row.startTime}
                            onChange={e => updateScheduleRow(index, { startTime: e.target.value })}
                            disabled={!row.enabled}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 140 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            size="small"
                            value={row.endTime}
                            onChange={e => updateScheduleRow(index, { endTime: e.target.value })}
                            disabled={!row.enabled}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 140 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={row.action}
                            onChange={e =>
                              updateScheduleRow(index, {
                                action: e.target.value as 'FORWARD' | 'AI_AGENT',
                              })
                            }
                            disabled={!row.enabled}
                            sx={{ minWidth: 200 }}
                          >
                            <MenuItem value="FORWARD">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Phone size={14} />
                                Forward to verified number
                              </Box>
                            </MenuItem>
                            <MenuItem value="AI_AGENT">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span style={{ fontSize: 14 }}>🤖</span>
                                AI Voice Call
                              </Box>
                            </MenuItem>
                          </TextField>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  disableElevation
                  onClick={handleSave}
                  disabled={isSaving || isSavingConfig || isSavingRule || !scheduleChanged}
                  startIcon={
                    isSaving ? <CircularProgress size={16} color="inherit" /> : <Save size={16} />
                  }
                >
                  {isSaving ? 'Saving...' : 'Save Schedule'}
                </Button>
              </Box>
            </Paper>
          )}
        </Box>
      )}

      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default RoutingView;
